// src/components/tasks/TaskDetailDrawer.jsx
import React, { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Drawer,
  Box,
  IconButton,
  Dialog,
  useMediaQuery,
  useTheme,
  Typography,
  AppBar,
  Toolbar,
  alpha,
} from '@mui/material';
import {
  Close as CloseIcon,
  ArrowBack as BackIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
} from '@mui/icons-material';

// Import components
import MinimalTaskDetails from './MinimalTaskDetails';
import useConfirmDialog from '../../hooks/useConfirmationDialog';
import useTaskStore from '../../stores/taskStore';

/**
 * TaskDetailDrawer - Displays task details in a drawer or dialog
 * Now uses the minimal TaskDetails component for better UX
 * Supports both mobile (dialog) and desktop (drawer) experiences
 */
const TaskDetailDrawer = ({ 
  taskId,
  open = false,
  onClose,
  mode = 'drawer',
  projectId: externalProjectId,
  sectionId: externalSectionId,
  hideProjectField = false, // Explicitly control project field visibility
  hideSectionField = false, // Explicitly control section field visibility
  containerProps = {},
  ...contentProps
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints?.down('md') || '(max-width: 960px)');
  
  // State to track if drawer is expanded
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Toggle expanded state
  const toggleExpand = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);
  
  // Confirmation dialog
  const [ConfirmDialog, confirm] = useConfirmDialog();
  
  // Get store methods and state
  const { 
    currentTask,
    taskCreateContext,
    taskDrawerContext,
    isTaskDrawerOpen,
    closeTaskDrawer: storeCloseDrawer
  } = useTaskStore();
  
  // Get taskId from URL params if not provided as prop
  const [searchParams] = useSearchParams();
  const urlTaskId = searchParams.get('taskId');
  
  // Use external open/close handlers or internal store handlers
  const isControlled = open !== undefined && onClose !== undefined;
  const isOpen = isControlled ? open : isTaskDrawerOpen;
  const handleClose = useCallback(() => {
    if (isControlled && onClose) {
      onClose();
    } else {
      storeCloseDrawer();
    }
  }, [isControlled, onClose, storeCloseDrawer]);
  
  // Get the actual taskId to use - from prop, URL, or currentTask
  const actualTaskId = taskId || urlTaskId || currentTask?._id;
  
  // Determine if we're in create mode - only when drawer is open for creation
  // If drawer is open without a task but also without taskCreateContext, it's loading a task
  const isCreateMode = !actualTaskId && isTaskDrawerOpen && 
    (taskCreateContext && Object.keys(taskCreateContext).length > 0);
  
  // Debug logging
  console.log('TaskDetailDrawer - Debug Info:', {
    propTaskId: taskId,
    urlTaskId: urlTaskId,
    currentTaskId: currentTask?._id,
    actualTaskId: actualTaskId,
    isCreateMode: isCreateMode,
    hasCurrentTask: !!currentTask,
    currentTaskTitle: currentTask?.title
  });
  
  // For desktop, use Drawer - For mobile, use Dialog
  if (fullScreen || mode === 'dialog') {
    return (
      <>
        <Dialog
          open={isOpen}
          onClose={handleClose}
          fullScreen={true}
          {...containerProps}
        >
          <AppBar
            position="static"
            elevation={0}
            sx={(theme) => ({
              backgroundColor: theme.palette.mode === 'dark'
                ? alpha(theme.palette.background.paper, 0.9)
                : theme.palette.background.paper,
              color: theme.palette.mode === 'dark'
                ? theme.palette.common.white
                : theme.palette.common.black,
              borderBottom: `1px solid ${theme.palette.mode === 'dark'
                ? alpha(theme.palette.background.light, 0.15)
                : theme.palette.divider
              }`,
            })}
          >
            <Toolbar>
              <IconButton
                edge="start"
                onClick={handleClose}
                aria-label="close"
                sx={(theme) => ({
                  color: theme.palette.mode === 'dark'
                    ? alpha(theme.palette.common.white, 0.8)
                    : alpha(theme.palette.common.black, 0.7),
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark'
                      ? alpha(theme.palette.secondary.main, 0.15)
                      : alpha(theme.palette.primary.main, 0.08),
                  }
                })}
              >
                <BackIcon />
              </IconButton>
              <Typography
                sx={(theme) => ({
                  ml: 2,
                  flex: 1,
                  fontWeight: 600,
                  color: theme.palette.mode === 'dark'
                    ? theme.palette.common.white
                    : theme.palette.common.black,
                })}
                variant="h6"
                component="div"
              >
                {isCreateMode ? "Create Task" : currentTask?.title || "Task Details"}
              </Typography>
            </Toolbar>
          </AppBar>
          
          <Box sx={(theme) => ({
            height: 'calc(100vh - 64px)',
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
            // Remove padding as component handles its own
            p: 0,
            // Improve scrollbar visibility
            '&::-webkit-scrollbar': {
              width: 8,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: theme.palette.mode === 'dark'
                ? alpha(theme.palette.background.light, 0.2)
                : alpha(theme.palette.background.light, 0.5),
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: theme.palette.mode === 'dark'
                ? alpha(theme.palette.background.default, 0.2)
                : alpha(theme.palette.background.light, 0.1),
            },
          })}>
            <MinimalTaskDetails
              taskId={actualTaskId}
              onClose={handleClose}
              isCreateMode={isCreateMode}
              initialValues={{
                ...taskCreateContext,
                projectId: externalProjectId || taskCreateContext?.projectId,
                sectionId: externalSectionId || taskCreateContext?.sectionId,
              }}
              onSubmit={contentProps.onSubmit}
              embedded={true}
              hideProjectField={hideProjectField || taskDrawerContext?.hideProjectField}
              hideSectionField={hideSectionField || taskDrawerContext?.hideSectionField}
            />
          </Box>
          
          <ConfirmDialog />
        </Dialog>
      </>
    );
  }

  // For desktop, use Drawer
  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={handleClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: isExpanded ? '100%' : { xs: '100%', sm: 600, md: 600 },
          maxWidth: '100%',
          display: 'flex',
          flexDirection: 'column',
          transition: theme => theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        },
        ...containerProps?.sx
      }}
      {...containerProps}
    >
      <Box sx={(theme) => ({
        p: 2,
        display: 'flex',
        justifyContent: 'space-between',
        borderBottom: 1,
        borderColor: theme.palette.mode === 'dark'
          ? alpha(theme.palette.background.light, 0.15)
          : theme.palette.divider,
        alignItems: 'center',
        backgroundColor: theme.palette.mode === 'dark'
          ? alpha(theme.palette.background.paper, 0.8)
          : alpha(theme.palette.background.paper, 0.95),
        backdropFilter: 'blur(8px)',
        // Add subtle shadow for better visibility
        boxShadow: theme.palette.mode === 'dark'
          ? '0 2px 4px rgba(0, 0, 0, 0.2)'
          : '0 1px 3px rgba(0, 0, 0, 0.06)',
        position: 'sticky',
        top: 0,
        zIndex: 10,
      })}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
          <IconButton
            size="small"
            onClick={handleClose}
            sx={(theme) => ({
              color: theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.8)
                : alpha(theme.palette.common.black, 0.7),
            })}
          >
            <BackIcon />
          </IconButton>
          
          <Typography
            variant="h6"
            sx={(theme) => ({
              fontWeight: 600,
              color: theme.palette.mode === 'dark'
                ? theme.palette.common.white
                : theme.palette.common.black,
            })}
          >
            {isCreateMode ? "Create Task" : currentTask?.title || "Task Details"}
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex' }}>
          <IconButton
            size="small"
            onClick={toggleExpand}
            sx={(theme) => ({
              mr: 1,
              color: theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.8)
                : alpha(theme.palette.common.black, 0.7),
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark'
                  ? alpha(theme.palette.secondary.main, 0.15)
                  : alpha(theme.palette.primary.main, 0.08),
              }
            })}
            aria-label={isExpanded ? "exit full screen" : "full screen"}
          >
            {isExpanded ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
          </IconButton>
          <IconButton
            size="small"
            onClick={handleClose}
            aria-label="close"
            sx={(theme) => ({
              color: theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.8)
                : alpha(theme.palette.common.black, 0.7),
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark'
                  ? alpha(theme.palette.error.main, 0.15)
                  : alpha(theme.palette.error.main, 0.08),
                color: theme.palette.error.main,
              }
            })}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
      
      <Box sx={(theme) => ({
        flexGrow: 1,
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
        // Remove padding as component handles its own
        p: 0,
        // Improve scrollbar visibility
        '&::-webkit-scrollbar': {
          width: 8,
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: theme.palette.mode === 'dark'
            ? alpha(theme.palette.background.light, 0.2)
            : alpha(theme.palette.background.light, 0.5),
          borderRadius: 4,
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: theme.palette.mode === 'dark'
            ? alpha(theme.palette.background.default, 0.2)
            : alpha(theme.palette.background.light, 0.1),
        },
      })}>
        <MinimalTaskDetails
          taskId={actualTaskId}
          onClose={handleClose}
          isCreateMode={isCreateMode}
          initialValues={{
            ...taskCreateContext,
            projectId: externalProjectId || taskCreateContext?.projectId,
            sectionId: externalSectionId || taskCreateContext?.sectionId,
          }}
          onSubmit={contentProps.onSubmit}
          embedded={true}
          hideProjectField={hideProjectField || taskDrawerContext?.hideProjectField}
          hideSectionField={hideSectionField || taskDrawerContext?.hideSectionField}
        />
      </Box>
      
      <ConfirmDialog />
    </Drawer>
  );
};

export default TaskDetailDrawer;